import angular, { IScope, ITimeoutService } from 'angular';
import { IFieldControllerField } from './field-controller';
import { IFormModel } from '../form-controller';
import { SharedAngular } from '../../@types/sharedAngular';

interface IAddressField extends IFieldControllerField {
  $dirty?: boolean;
}

interface IAddressFieldScope extends IScope {
  field: IAddressField;
  form: IFormModel;
  formCtrl?: {
    formModelId?: string;
    isPublic?: boolean;
  };
  options: any; // keeping this as 'any' due to kendo UI typings
  addressDisplayValue: string;
  isLoading: boolean;
}

angular
  .module('fg')
  .directive('fgFieldAddress', [
    'fgFieldAddressLinkFn',
    function (fgFieldAddressLinkFn) {
      return {
        replace: true,
        templateUrl: 'angular-form-gen/field-templates/default/address.ng.html',
        scope: true,
        link: fgFieldAddressLinkFn,
        require: ['^?fgForm']
      };
    }
  ])
  .factory('fgFieldAddressLinkFn', [
    '$timeout',
    'addressLookupService',
    function (
      $timeout: ITimeoutService,
      addressLookupService: SharedAngular.AddressLookupService
    ) {
      return function ($scope: IAddressFieldScope, $element, $attrs, ctrls) {
        const fgFormCtrl = ctrls[0];
        const field = $scope.field;
        const form = $scope.form;
        const schema = field.schema;
        let isSelectedFromList = false;
        let currentSearchText = '';

        if (!form.data[schema.name]) {
          form.data[schema.name] = null;
        }

        $scope.addressDisplayValue = '';
        $scope.isLoading = false;
        const MIN_QUERY_LENGTH = 3;

        initializeFromSavedData();

        const isPublicForm = fgFormCtrl?.isPublic === true;
        const businessId = fgFormCtrl?.businessId || '';

        const addressDataSource = new kendo.data.DataSource({
          serverFiltering: true,
          transport: {
            read: function (options) {
              const searchText = options.data.filter?.filters?.[0]?.value || '';

              if (searchText.length < MIN_QUERY_LENGTH) {
                options.success([]);
                return;
              }

              $scope.isLoading = true;

              const searchPromise = isPublicForm
                ? addressLookupService.searchAddressesForPublicForm(
                    searchText,
                    schema.addressProviderId,
                    businessId,
                    0,
                    10
                  )
                : addressLookupService.searchAddresses(
                    searchText,
                    schema.addressProviderId,
                    0,
                    10
                  );

              searchPromise
                .then(function (results) {
                  const kendoData = results.map(function (item) {
                    return {
                      addressId: item.addressId,
                      fullAddress: item.fullAddress
                    };
                  });
                  options.success(kendoData);
                })
                .catch(function (error) {
                  console.error('Address search error:', error);
                  options.success([]);
                })
                .finally(function () {
                  $scope.isLoading = false;
                });
            }
          }
        });

        $scope.options = {
          dataSource: addressDataSource,
          dataTextField: 'fullAddress',
          filter: 'contains',
          minLength: MIN_QUERY_LENGTH,
          placeholder: schema.placeholder,
          select: function (e) {
            const dataItem = e.dataItem;
            if (dataItem?.addressId) {
              isSelectedFromList = true;
              $scope.isLoading = true;

              const detailsPromise = isPublicForm
                ? addressLookupService.getAddressDetailsForPublicForm(
                    dataItem.addressId,
                    schema.addressProviderId,
                    businessId
                  )
                : addressLookupService.getAddressDetails(
                    dataItem.addressId,
                    schema.addressProviderId
                  );

              detailsPromise
                .then(function (details) {
                  const formattedAddress =
                    addressLookupService.formatAddress(details);
                  e.sender.value(formattedAddress);
                  currentSearchText = formattedAddress;
                  $scope.addressDisplayValue = formattedAddress;
                  form.data[schema.name] = JSON.stringify(details);

                  if (field.state) {
                    field.state.$setValidity('addressSelection', true);
                  }
                  field.$dirty = true;
                })
                .catch(function (error) {
                  console.error('Failed to get address details:', error);
                  if (field.state) {
                    field.state.$setValidity('addressSelection', false);
                  }
                })
                .finally(function () {
                  $scope.isLoading = false;
                });
            }
          },
          change: function (e) {
            const value = e.sender.value();

            if (isSelectedFromList && value !== currentSearchText) {
              isSelectedFromList = false;
              form.data[schema.name] = null;
              field.$dirty = true;
            }

            if (!value?.trim()) {
              isSelectedFromList = false;
              form.data[schema.name] = null;
              currentSearchText = '';
              $scope.addressDisplayValue = '';

              if (field.state) {
                field.state.$setValidity('addressSelection', true);
              }
            }

            validateAddress();
          },
          close: function () {
            $timeout(validateAddress, 0);
          }
        };

        function validateAddress() {
          const inputValue = $element.find('input').val();
          if (inputValue?.trim() && !isSelectedFromList) {
            if (field.state) {
              field.state.$setValidity('addressSelection', false);
              field.state.$setDirty();

              if (!$scope.$$phase) {
                $scope.$apply();
              }
            }
            return false;
          }

          if (field.state) {
            field.state.$setValidity('addressSelection', true);
          }
          return true;
        }

        $scope.$on('fg:validate', function () {
          validateAddress();
        });

        function initializeFromSavedData(): void {
          const savedData = form.data[schema.name];
          if (!savedData) return;

          try {
            const savedAddress = JSON.parse(savedData);
            if (addressLookupService.isValidAddress(savedAddress)) {
              $scope.addressDisplayValue =
                addressLookupService.formatAddress(savedAddress);
              currentSearchText = $scope.addressDisplayValue;
              isSelectedFromList = true;
            }
          } catch {
            form.data[schema.name] = null;
          }
        }
      };
    }
  ]);
